import { z } from 'zod';

import { proofExtractionObjectClassSchema } from './contracts.js';
import { proofSourceBundleRequestSchema } from './source-bundle.js';

export const ADMIN_API_ERROR_SCHEMA_VERSION = 'admin-api-error/v1' as const;
export const PROOF_OPERATOR_API_SCHEMA_VERSION = 'proof-operator-api/v1' as const;
export const PROOF_OPERATOR_CAPABILITY_SCHEMA_VERSION = 'proof-operator-capabilities/v1' as const;
export const PROOF_EVIDENCE_PREVIEW_SCHEMA_VERSION = 'proof-evidence-preview/v1' as const;

const uuidSchema = z.string().uuid();
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
const redactedHashSchema = z.string().regex(/^[a-f0-9]{12}$/);
const hasControlCharacters = (value: string) => [...value].some((character) => {
  const codePoint = character.charCodeAt(0);
  return codePoint <= 31 || codePoint === 127;
});
const destructiveReasonSchema = z.string().trim().min(8).max(500)
  .refine((value) => !hasControlCharacters(value));
const safeDetailValueSchema = z.union([
  z.string().max(160),
  z.number().finite(),
  z.boolean(),
  z.null(),
]);
const proofEvidenceSourceFormatSchema = z.enum([
  'PDF',
  'IMAGE',
  'CSV',
  'XLSX',
  'DOCX',
  'PPTX',
  'TEXT',
  'MARKDOWN',
  'HTML',
  'SOURCE',
]);

export const adminApiErrorEnvelopeSchema = z.object({
  ok: z.literal(false),
  error: z.object({
    schemaVersion: z.literal(ADMIN_API_ERROR_SCHEMA_VERSION),
    code: z.string().regex(/^[a-z][a-z0-9_]{0,119}$/),
    message: z.string().min(1).max(240),
    retryable: z.boolean(),
    requestId: z.string().regex(/^[A-Za-z0-9_-]{8,80}$/),
    safeDetails: z.record(safeDetailValueSchema).optional(),
  }).strict(),
}).strict();

export const proofOperatorCapabilitySchema = z.object({
  schemaVersion: z.literal(PROOF_OPERATOR_CAPABILITY_SCHEMA_VERSION),
  role: z.literal('proof_admin'),
  manualProof: z.object({ available: z.literal(true) }).strict(),
  extraction: z.object({
    status: z.enum(['enabled', 'disabled', 'misconfigured']),
    visible: z.boolean(),
    capabilities: z.object({
      ingest: z.boolean(),
      urlIngest: z.boolean(),
      analysis: z.boolean(),
      review: z.boolean(),
      drafts: z.boolean(),
    }).strict(),
  }).strict(),
}).strict();

export const proofEvidencePreviewRequestSchema = z.object({
  runId: uuidSchema,
  runGeneration: z.number().int().positive(),
  candidateId: uuidSchema,
  candidateRevision: z.number().int().positive(),
  candidateFingerprint: sha256Schema,
  evidenceRef: redactedHashSchema,
}).strict();

const proofRunIdentityRequestShape = {
  runId: uuidSchema,
  runGeneration: z.number().int().positive(),
};

export const proofRunRequestSchema = z.union([
  z.object({
    action: z.literal('create'),
    bundle: proofSourceBundleRequestSchema,
  }).strict(),
  z.object({
    action: z.literal('queue'),
    ...proofRunIdentityRequestShape,
  }).strict(),
  z.object({
    action: z.literal('control'),
    ...proofRunIdentityRequestShape,
    control: z.enum(['cancel', 'retry']),
    confirmed: z.literal(true),
    reason: destructiveReasonSchema,
  }).strict(),
  z.object({
    action: z.literal('control'),
    ...proofRunIdentityRequestShape,
    control: z.enum(['exclude_asset', 'exclude_object']),
    targetRef: redactedHashSchema,
    confirmed: z.literal(true),
    reason: destructiveReasonSchema,
  }).strict(),
]);

export const proofUploadRequestSchema = z.union([
  z.object({
    action: z.literal('issue'),
    runId: uuidSchema,
    filename: z.string().min(1).max(255),
    declaredMimeType: z.string().min(1).max(160),
    expectedBytes: z.number().int().positive(),
    idempotencyKey: z.string().min(16).max(200),
  }).strict(),
  z.object({
    action: z.enum(['finalize', 'cancel']),
    uploadId: uuidSchema,
  }).strict(),
  z.object({ action: z.literal('cleanup') }).strict(),
]);

export const proofUrlSnapshotRequestSchema = z.object({
  runId: uuidSchema,
  url: z.string().min(1).max(2_048),
  idempotencyKey: z.string().min(16).max(200),
}).strict();

export const proofEvidencePreviewSchema = z.object({
  schemaVersion: z.literal(PROOF_EVIDENCE_PREVIEW_SCHEMA_VERSION),
  binding: z.object({
    runId: uuidSchema,
    runGeneration: z.number().int().positive(),
    candidateId: uuidSchema,
    candidateRevision: z.number().int().positive(),
    candidateFingerprint: sha256Schema,
    evidenceSetRef: redactedHashSchema,
  }).strict(),
  evidence: z.object({
    evidenceRef: redactedHashSchema,
    sourceLabel: z.string().min(1).max(80),
    sourceFormat: proofEvidenceSourceFormatSchema,
    objectClass: z.union([proofExtractionObjectClassSchema, z.literal('unknown')]),
    support: z.enum(['entails', 'operand', 'caveat', 'counterevidence']),
    locator: z.object({
      kind: z.enum(['text_span', 'cell_range', 'formula', 'bounding_box', 'object_path', 'unknown']),
      label: z.string().min(1).max(160),
    }).strict(),
    canonicalReady: z.literal(true),
  }).strict(),
  governance: z.object({
    deterministicVerifier: z.enum(['current', 'stale']),
    humanSemanticReview: z.enum(['pending', 'accepted', 'rejected']),
    aiDraftAuthorization: z.enum(['none', 'active', 'stale', 'revoked']),
    modelConfidenceIsEvidence: z.literal(false),
  }).strict(),
}).strict();

export type AdminApiErrorEnvelope = z.infer<typeof adminApiErrorEnvelopeSchema>;
export type ProofOperatorCapability = z.infer<typeof proofOperatorCapabilitySchema>;
export type ProofEvidencePreviewRequest = z.infer<typeof proofEvidencePreviewRequestSchema>;
export type ProofEvidencePreview = z.infer<typeof proofEvidencePreviewSchema>;

export type ProofApiErrorKind = 'auth_expired' | 'stale' | 'retryable' | 'terminal';

export function classifyProofApiError(code: string, retryable: boolean): ProofApiErrorKind {
  if (code === 'unauthorized') return 'auth_expired';
  if (code.startsWith('stale_')) return 'stale';
  return retryable ? 'retryable' : 'terminal';
}
